import type { AiCapabilityPage } from "../schema.js";
import { LIVE, MANAGER_ROLES, WRITE_FLAGS, SLOT_COLLECTION_AUTOFILL, SLOT_GROUP } from "./_shared.js";

/**
 * `RECORD-PAID` (@UC0200) — thủ quỹ GHI NHẬN một thành viên đã đóng (tiền mặt / chuyển không qua lời báo), T2.
 * Executor = `RecordPaidService.recordPaid` (payments/record-paid.service.ts:186) — ghi thẳng bước (3) §3.7 vì người ghi là
 * người có thẩm quyền; vẫn qua phiếu xác nhận đầy đủ (T2) vì đây là bút toán. Binding gọi `recordPaidInTx` (cùng lõi, tx của phiếu).
 *
 * 🔴🔴 **TRANG GÁNH CAM KẾT #4 "hai Minh" CỦA EPIC** (QĐ điều phối 18/09). `0033` không gánh được: trang đó có **0 ô
 * `type:"member"`**, ô `danhSach` là tên MỚI để TẠO suất. Ở đây ô `nguoi` là **tham chiếu tới người ĐANG CÓ**, nên CLAUDE.md §4
 * áp thẳng vào một đường GHI TIỀN: *đoán `memberId` khi 0 hoặc ≥2 khớp là CẤM, phải HỎI*. Hai nửa của luật, đừng gỡ nửa nào:
 *  · **nửa Sổ** — ô `nguoi` KHÔNG khai `autofill` (validator `SLOT_AUTOFILL_TYPE` cấm ô `member` tự điền) và trang có câu
 *    `clarify` của ô `nguoi`. Ô người trống ⇒ resolver bày TOÀN BỘ ứng viên, **không tự điền kể cả khi nhóm chỉ có một người**:
 *    một người vào bút toán phải do người dùng CHỈ (`member-slot.ts`), khác hẳn ô `quy`/`khoan` (duy nhất ⇒ lấy luôn).
 *  · **nửa hành vi** — `ai-doer-record-paid.int-spec.ts`: hai ghế cùng tên "Minh" + "Minh đóng rồi" ⇒ lượt HỎI kèm đúng hai
 *    ứng viên, `ledger_lines` KHÔNG đổi; chọn người THỨ HAI ⇒ suất người thứ nhất vẫn `pending`.
 *
 * 🔴 **Ô `nhom` không phải trang trí** (QĐ PO 17/09 16:05): tab trợ lý là tab TOÀN CỤC. Không khai ô nhóm thì `assertCanOpen`
 * trả 403 `GROUP_REQUIRED`; và riêng trang này còn nặng hơn — `SlotResolverService.loadMembers` trả `undefined` khi
 * `groupId === null`, tức ô `nguoi` có **0 ứng viên** và lượt hỏi không bao giờ kết thúc. Thứ tự "gợi ý > duy nhất > hỏi" là
 * luật của ENGINE (0023b); trang chỉ KHAI ô, và khai nó ĐẦU TIÊN vì phạm vi hai ô sau phụ thuộc nhóm đã chốt.
 *
 * 🔴 **KHÔNG có ô ngày, cố ý.** `_recordInTx` đóng cứng `recordedAt = new Date()` (`record-paid.service.ts:697`) — một ô `ngay`
 * ở đây là ô người dùng điền rồi bị vứt, và phiếu in một ngày khác ngày sẽ vào sổ (`@UC0163`). Lùi ngày một bút toán là việc
 * của task khác (đụng §3.2/§3.3), không phải của trang. Trang **không hỏi ngày nhưng PHẢI NÓI ngày** — fact `ngayGhi`, đúng
 * khuôn `hanGhi` của `COL-EQUAL`.
 *
 * 🔴 **`duGhi` — PHẦN ĐÓNG DƯ phải hiện trên phiếu** (review #853 vòng 2 M2, AI-INV-4). Đóng 600k cho suất
 * 500k với policy `credit` ⇒ 100k thành một dòng `debt_entries` **append-only** + bút toán `ADVANCE_CREDIT`:
 * **quỹ vừa nhận thêm một nghĩa vụ nợ lại thành viên**. Phiếu chỉ nói *"Ghi vào sổ … / Còn lại 0 đ"* là giấu
 * mất điều đó. Hệ quả KHÁC nhau theo policy (`credit` = quỹ nợ lại · `refund` = chờ hoàn, không vào quỹ) mà
 * template là chữ TĨNH, nên câu chữ nằm trong GIÁ TRỊ của fact; không dư ⇒ "0 đ".
 *
 * 🔴 **`conLai` là fact BẮT BUỘC, không phải trang trí.** Ghi 200k cho suất 500k ⇒ due thành `partially_paid`. Phiếu giấu số
 * còn lại ⇒ thủ quỹ đóng sổ tin là xong ⇒ một suất thiếu 300k không ai đòi. Số này chỉ có ở tầng dữ liệu, nên nó phải là fact
 * của executor, không phải câu chữ suy từ ô.
 *
 * 🔴 **`confirmationSource` = `admin_one_party`, KHÔNG `ai`** (QĐ-1/QĐ-2 B0 19/09; PO lật được): cột này trên
 * `ledger_transactions` (**append-only**) ghi **hình dạng bằng chứng** — *một bên hay hai bên* — chứ không ghi KÊNH. Kênh lấy
 * lại được (`ai_audit_log` + khoá idempotency tất định nối chat → sổ); hình dạng thì không. Và cùng một sự việc ngoài đời
 * (một thủ quỹ xác nhận một suất) mà ghi hai giá trị tuỳ chat/form là **báo cáo lệch theo kênh** — gãy cam kết #3.
 * ⚠️ Enum `CONFIRMATION_SOURCE.AI` vẫn tồn tại cho cửa khác — đừng gỡ nó, chỉ đừng dùng ở đây.
 *
 * 🔴 **§3.7 giữ nguyên:** đây là đường **O/A/T**, KHÔNG nằm trong ngoại lệ `self_payment_auto_confirm` (nới đó dành cho người
 * TỰ khai cho suất mình đại diện). Trang không đọc cờ đó và không được đọc.
 */
export const RECORD_PAID = {
  id: "RECORD-PAID",
  uc: "@UC0200",
  tier: "T2",
  status: LIVE,
  roles: MANAGER_ROLES,
  flags: WRITE_FLAGS,
  slots: [
    SLOT_GROUP,
    // 🔴 Cam kết #4: KHÔNG `autofill`. Ô này là tham chiếu tới người ĐANG CÓ ⇒ 0 hoặc ≥2 khớp thì HỎI (`clarify` của ô).
    { id: "nguoi", type: "member", required: true, ask: "Ai đã đóng?", clarify: "Có nhiều người tên giống vậy — bạn nói ai?" },
    { ...SLOT_COLLECTION_AUTOFILL, id: "khoan", required: true, ask: "Đóng cho khoản nào?" },
    { id: "soTien", type: "money", required: false, ask: "Đóng bao nhiêu? (bỏ trống = đủ số còn phải đóng)" },
  ],
  llm: {
    /*
     * 🔴 US-AIV2-0112 H08 (@UC0200 @UC0197, lô Sổ 3): câu ranh giới cuối mô tả — người quản lý nói "tôi/mình đóng…" là TỰ KHAI cho suất
     * của chính mình (PAY-REPORT; O/A/T vào sổ ngay theo §3.7 vế vai), không phải ghi nhận NGƯỜI KHÁC. Đo LLM thật n=3: 2/3·0/3·0/3 ⇒
     * 3/3 cả ba câu, chiều ngược 3/3. Chữ chọn bằng top-8 offline — câu NGẮN NHẤT còn đạt (27 ca đổi TẬP, 0 đích rơi; bản dài 39–156 ca).
     * Đừng viết dài thêm mà không đo lại (`_runs/US-AIV2-0112/SO-LO3/`).
     */
    description: "Người quản lý ghi nhận rằng một thành viên đã đóng tiền cho một khoản — vào sổ quỹ thật sau khi xác nhận. Người nói tự đóng ⇒ PAY-REPORT.",
    examples: [
      "ghi nhận Minh đã đóng 500k",
      "ghi nhan minh da dong 500k",
      "Lan đã nộp tiền quỹ tháng 9 bằng tiền mặt",
      "lan da nop tien mat",
      "đánh dấu Hùng đã đóng đợt thu dã ngoại",
      "danh dau hung da dong",
      "Minh đóng rồi",
      "ghi Lan đã đóng",
      "bé An đã nộp 200k",
      "ghi nhận đã đóng",
      "chị Hoa đưa tiền mặt 300k rồi",
      "Hùng nộp 500k",
      // ——— 0105: thêm cho đủ 40 ———
      "danh dau hung da dong dot thu da ngoai",
      "ghi lan da dong",
      "be an da nop 200k",
      "ghi nhan da dong",
      "chi hoa dua tien mat 300k roi",
      "hung nop 500k",
      "ghi nhận An đã nộp đủ tiền quỹ",
      "ghi nhan an da nop du tien quy",
      "đánh dấu Lan đã đóng khoản tháng này",
      "danh dau lan da dong khoan thang nay",
      "Bình đưa tiền mặt rồi, ghi vào sổ giúp",
      "binh dua tien mat roi ghi vao so giup",
      "vào sổ cho Minh đóng 400k",
      "vao so cho minh dong 400k",
      "phụ huynh bé Hùng nộp 250k tiền mặt",
      "phu huynh be hung nop 250k tien mat",
      "ghi nhận Lan đóng một nửa thôi",
      "ghi nhan lan dong mot nua thoi",
      "An đưa 150k, ghi cho khoản dã ngoại",
      "an dua 150k ghi cho khoan da ngoai",
      "tích cho Bình là đã đóng",
      "tich cho binh la da dong",
      "ghi nhận thêm một người đã đóng",
      "ghi nhan them mot nguoi da dong",
      "cập nhật là bạn đó đã nộp tiền",
      "cap nhat la ban do da nop tien",
      "vào sổ khoản Hùng vừa đưa",
      "vao so khoan hung vua dua",
      "ghi nhận Minh đã đóng đủ rồi nhé",
    ],
    // 🔴 US-AIV2-0111 C06 (@UC0320) — câu HỎI (việc XEM) không thuộc trang ghi này; mỗi câu là ví dụ của một trang đọc ⇒ eval đo được.
    questionsNotHere: ["bao nhiêu người đã đóng rồi", "ai chưa đóng"],
  },
  /**
   * 🔴 US-AIV2-0110 S03 (@UC0260 · QĐ PO 4) — ĐÁP ÁN TIỀN của bộ eval, VIẾT TAY từ chính câu (không từ đầu ra model).
   * KHÔNG vào prompt ⇒ không đổi hash theo ca. Câu vắng mặt ở đây: không nói số tiền, hoặc mơ hồ bậc (§4).
   */
  evalAmounts: {
    "ghi nhận Minh đã đóng 500k": "500000",
    "ghi nhan minh da dong 500k": "500000",
    "bé An đã nộp 200k": "200000",
    "chị Hoa đưa tiền mặt 300k rồi": "300000",
    "Hùng nộp 500k": "500000",
    "be an da nop 200k": "200000",
    "chi hoa dua tien mat 300k roi": "300000",
    "hung nop 500k": "500000",
    "vào sổ cho Minh đóng 400k": "400000",
    "vao so cho minh dong 400k": "400000",
    "phụ huynh bé Hùng nộp 250k tiền mặt": "250000",
    "phu huynh be hung nop 250k tien mat": "250000",
    "An đưa 150k, ghi cho khoản dã ngoại": "150000",
    "an dua 150k ghi cho khoan da ngoai": "150000",
  },
  executor: "recordPaid.recordPaid",
  // #811 M1: ô `soTien` TUỲ CHỌN ⇒ phiếu BÚT TOÁN phải hiện con số executor SẼ GHI (fact `soTienGhi`), không hiện ô trống.
  // 🔴 MED-1 #832 — ca THỨ BA của luật: khoá ledger của `recordPaid` DERIVE TỪ NỘI DUNG (due + actor + số), nên cùng người +
  // cùng khoản + cùng số ở HAI hội thoại ⇒ REPLAY: 0 dòng mới, 0 đồng cộng thêm, và facts BẰNG phiếu nên không phép so nào
  // phát hiện. Không có `doneAlready` thì lượt đó nói "Sổ quỹ đã cộng" cho một lần ghi không tồn tại.
  executorMayNoop: true,
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: O/A/T ghi nhận đã đóng — trạng thái (3) của §3.7. */
  endpoints: [
    "POST /member-dues/{memberDueId}/record-paid",
    "POST /member-dues/record-paid-batch",
  ],
  facts: ["tenNguoi", "tenDot", "soTienGhi", "conLai", "conLaiThat", "duGhi", "ngayGhi", "ngayGhiThat"],
  templates: {
    // Thứ tự chỗ trống = thứ tự DÒNG PHIẾU (`renderConfirm` đọc `listPlaceholders`): nhóm → người → khoản → số ghi → còn lại → ngày.
    confirm: "Ở nhóm {nhom}, ghi nhận {tenNguoi} đã đóng cho khoản “{tenDot}” số tiền {soTienGhi} — sau khi ghi còn lại {conLai}, đóng dư {duGhi}, ngày ghi {ngayGhi}. Đồng ý thì mình vào sổ quỹ ngay.",
    /**
     * 🔴 review #884 HIGH-1 — câu này chạy SAU khi bút toán đã commit nên nó nói **ngày THẬT** (`{ngayGhiThat}`,
     * đọc từ `recordedAt` service trả về), KHÔNG nhắc lại lời hứa không-cụ-thể của phiếu. Dùng `{ngayGhi}` ở đây
     * sau QĐ PO 20/09 (A4) cho ra chữ hỏng — *"ngày ngày bạn bấm Đồng ý"* — và làm mất luôn thông tin ngày.
     * Cùng luật với `{hanThat}` của `COL-EQUAL` và `{conLaiThat}` dưới: khoá CHỈ-CÓ-Ở-EXECUTE, không vào vòng so.
     */
    done: "Đã ghi {tenNguoi} đóng {soTienGhi} cho “{tenDot}” ngày {ngayGhiThat}. Sổ quỹ đã cộng; suất này còn lại {conLai}.",
    /**
     * 🔴 Lượt KHÔNG ghi gì. Ba việc câu này phải làm, theo thứ tự người đọc cần:
     *  1. nói thẳng là sổ **KHÔNG cộng thêm** — người dùng không được phải suy ra từ chữ "đã";
     *  2. chỉ ra số ĐÃ ghi trước đó và số CÒN NỢ THẬT, để họ biết mình đang đứng ở đâu;
     *  3. mở **cửa thoát**: muốn ghi một lần đóng NỮA thì ghi ở nút “Nộp quỹ” của nhóm (khoá theo phiên màn, mỗi lần mở là một lần ghi).
     *     🔴 QĐ người dùng 15/10/2026 (b): BỎ câu ví dụ cũ *"ghi nhận {tenNguoi} đã đóng {conLaiThat}"* — nó MỜI gõ lại đúng số còn nợ, và khi
     *     số đó trùng số vừa ghi (đóng 250k của 500k ⇒ còn 250k) thì khoá nghiệp vụ (ký trên số tiền, §3.4 — giữ nguyên, không đưa trạng-thái-trước
     *     vào khoá) lại trùng ⇒ lại lượt KHÔNG ghi ⇒ vòng lặp. Câu không gợi ý một con số nào để gõ.
     * CỐ Ý không nói "Sổ quỹ đã cộng" — đó chính là lời nói dối mà `doneAlready` sinh ra để xoá.
     *
     * 🔴 review #853 HIGH-1 — câu này dùng `{conLaiThat}`, **KHÔNG** `{conLai}`, và khác biệt đó là TIỀN:
     * `conLai` là *"còn lại SAU khi ghi"* — đúng cho phiếu (lượt CÓ ghi), **sai cho lượt KHÔNG ghi**. Đo thật:
     * suất 500k đã ghi 200k ⇒ nợ thật **300k**, còn `conLai` = 300k − 200k = **100k**. Bản cũ in 100k rồi mời
     * người dùng gõ lại chính con số sai đó. `conLaiThat` do executor đo TRONG tx lúc chốt, và chỉ có mặt ở
     * lượt không-ghi (nên nó KHÔNG bao giờ được dùng trong `confirm`/`done` — có test khoá cả hai chiều).
     */
    doneAlready:
      "Khoản “{tenDot}” của {tenNguoi} đã được ghi {soTienGhi} trước đó rồi, nên lần này sổ quỹ KHÔNG cộng thêm đồng nào. Suất này còn nợ {conLaiThat}. Nếu vừa có thêm một lần đóng nữa, bạn mở nhóm, bấm “Nộp quỹ” để ghi nhận lần đóng đó nhé.",
    /** 🔴 Cam kết #4 — câu chữ khi ≥2 người khớp tên. Nó ở Sổ chứ không ở engine: đổi lời mời là việc của PO, không phải của code. */
  },
  /** P5.3 — nhãn NGƯỜI ĐỌC cho từng dòng phiếu T2; thiếu một nhãn thì `renderConfirm` ném (không camelCase lên màn tiền). */
  labels: {
    nhom: "Nhóm",
    tenNguoi: "Người đóng",
    tenDot: "Khoản",
    soTienGhi: "Ghi vào sổ",
    conLai: "Còn lại sau khi ghi",
    duGhi: "Đóng dư",
    ngayGhi: "Ngày ghi",
  },
  journey: { outcomes: { done: { next: ["READ-COLLECTIONS", "DEBT-REMIND"] }, done_already: { next: ["READ-COLLECTIONS"] } } },
  help: { title: "Ghi nhận thành viên đã đóng", tryNow: "ghi nhận Minh đã đóng 500k" },
} satisfies AiCapabilityPage;
